import React from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Area, LabelList, Scatter } from 'recharts'
import useGetVsic from '../../../../../../common/category/useGetVsic'
import { ChartContainer } from '../../../../../../common/chart/chartContainer'
import { Element } from '../../../../../../common/html/element'
import { Loading } from '../../../../../../common/loading'
import { Notification } from '../../../../../../common/notification'
import { formatText } from '../../../../../../helpers/value'
import { EMPTY_VALUE } from '../../../../../../utils/common'
import {
  selectCompareIndustry,
  selectDefaultIndustry,
} from '../../../store/slice'
import { selectDistributionScoreData, selectLoading } from '../../store/slice'

const DistributionOfScore = ({ isDynamicReport, settingDynamic = {} }) => {
  const loading = useSelector(selectLoading('distributionScore'))
  const loadingCompare = useSelector(selectLoading('distributionScoreCompare'))
  const data = useSelector(selectDistributionScoreData)
  const vsicDefaultStore = useSelector(selectDefaultIndustry)
  const vsicCompareStore = useSelector(selectCompareIndustry)
  const { vsics } = useGetVsic()
  const { DEFAULT_INDUSTRY: vsicDefaultDyn, COMPARE_INDUSTRY: vsicCompareDyn } =
    settingDynamic
  const vsicDefault = isDynamicReport ? vsicDefaultDyn : vsicDefaultStore
  const vsicCompare = isDynamicReport ? vsicCompareDyn : vsicCompareStore

  const TYPE_CHART_RISK = [
    {
      name: 'industry',
      id: 'defaultMedian',
      color: '#00A8B5',
      dataKey: 'defaultMedian',
      dataFloatBar: 'dataFloatBarDefault',
      position: vsicCompare ? 'left' : 'top',
    },
    {
      name: 'region',
      id: 'compareMedian',
      color: '#E6964C',
      dataKey: 'compareMedian',
      dataFloatBar: 'dataFloatBarCompare',
      position: 'right',
    },
  ]

  const renderCustomContent = ({ data, renderTooltipLabel }) => {
    const titleDefaultIndustry = vsics.find(
      (item) => item.id === vsicDefault,
    )?.name
    const titleComparingIndustry = vsics.find(
      (item) => item.id === vsicCompare,
    )?.name
    return (
      <div>
        <div>{renderTooltipLabel()}</div>
        <div>
          <table>
            <tbody>
              <tr>
                <td></td>
                <td>
                  <Translate value="companyAnalysis.industryAnalysis.MEDIAN_SCORE" />
                </td>
                <td>
                  <Translate value="companyAnalysis.industryAnalysis.MIN_MAX" />
                </td>
              </tr>
              <tr>
                <td style={{ maxWidth: 250 }}>{titleDefaultIndustry}</td>
                <td style={{ textAlign: 'end' }}>
                  {formatText(data.defaultMedian)}
                </td>
                <td style={{ textAlign: 'end' }}>
                  {(data.dataFloatBarDefault || [])
                    .filter(Boolean)
                    .join(' - ') || EMPTY_VALUE}
                </td>
              </tr>
              {vsicCompare && (
                <tr>
                  <td style={{ maxWidth: 250 }}>{titleComparingIndustry}</td>
                  <td style={{ textAlign: 'end' }}>
                    {formatText(data.compareMedian)}
                  </td>
                  <td style={{ textAlign: 'end' }}>
                    {data.dataFloatBarCompare.filter(Boolean).join(' - ') ||
                      EMPTY_VALUE}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        marginTop: 12,
      }}
    >
      <Element style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
        <Translate value="companyAnalysis.industryAnalysis.DISTRIBUTION_OF_SCORE" />
      </Element>
      {loading || loadingCompare ? (
        <div className="positionRelative" style={{ height: 350 }}>
          <Loading />
        </div>
      ) : (
        <div id="distributionOfScoreChartId">
          {data && data.length ? (
            <ChartContainer
              data={data}
              width="100%"
              height={350}
              margin={{
                right: 10,
                bottom: 6,
              }}
              yAxis={[
                {
                  yAxisId: 'left',
                  dataKeys: ['maxValue', 'minValue'],
                  ticks: [0, 200, 400, 600, 800, 1000],
                  decimalLength: 0,
                },
              ]}
              xAxis={[
                {
                  dataKey: 'year',
                  padding: vsicCompare
                    ? { left: 35, right: 35 }
                    : { left: 15, right: 15 },
                  tickMargin: 3,
                  height: 18,
                  type: 'category',
                },
              ]}
              tooltip={{
                schema: {
                  renderCustomContent: renderCustomContent,
                },
              }}
            >
              {TYPE_CHART_RISK.map((item) => (
                <React.Fragment key={item.id}>
                  <Area
                    yAxisId="left"
                    dataKey={item.dataFloatBar}
                    activeDot={false}
                    fill={item.color}
                    fillOpacity={0.2}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                    stroke={item.color}
                  />
                  <Scatter
                    yAxisId="left"
                    zAxisId={'year'}
                    dataKey={item.dataKey}
                    fill={item.color}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey={item.dataKey}
                      position={item.position}
                    />
                  </Scatter>
                </React.Fragment>
              ))}
            </ChartContainer>
          ) : (
            <div style={{ height: 300, position: 'relative' }}>
              <Notification />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DistributionOfScore
